import { useClient } from '@cortex/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type CSSProperties } from 'react';

import { BackButton } from '../../../components/common/BackButton';
import { PageHeader } from '../../../components/common/PageHeader';
import { routes } from '../../../lib/constants';
import { createOrg } from '../../../lib/orgs';
import { guardSuperuser } from '../index';

export const Route = createFileRoute('/internal/organizations/new')({
  beforeLoad: () => guardSuperuser(),
  component: NewOrgPage,
});

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };
const grid2: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--sp-3)',
};

function NewOrgPage() {
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [name, setName] = useState('');
  const [alias, setAlias] = useState('');
  const [domain, setDomain] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createOrg(client, {
        name,
        alias: alias.trim() || slugify(name),
        domain: domain.trim() || undefined,
      }),
    onSuccess: async () => {
      try {
        await qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
      } catch (err) {
        console.error(err);
      }
      try {
        await nav({ to: routes.INTERNAL_ORGS });
      } catch (err) {
        console.error(err);
      }
    },
  });

  return (
    <>
      <PageHeader
        title="Create organization"
        actions={<BackButton to={routes.INTERNAL_ORGS} label="Organizations" />}
      />
      <div className="card" style={{ maxWidth: 720 }}>
        <div className="card-body">
          <form
            style={stack3}
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) create.mutate();
            }}
          >
            <div style={grid2}>
              <label className="field">
                <span className="field-label">Name</span>
                <input
                  required={true}
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Acme Robotics"
                />
              </label>
              <label className="field">
                <span className="field-label">Alias (slug)</span>
                <input
                  className="input"
                  value={alias}
                  onChange={(e) => setAlias(e.target.value)}
                  placeholder="acme-robotics"
                />
                <span className="field-help">Auto-derived from name if blank.</span>
              </label>
            </div>
            <label className="field">
              <span className="field-label">Verified email domain (optional)</span>
              <input
                className="input"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="acme.com"
              />
              <span className="field-help">
                When set, users with this email domain auto-join the org on first sign-in.
              </span>
            </label>
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              <button type="submit" className="btn btn--primary" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create'}
              </button>
              {create.error ? (
                <span
                  className="t-xs"
                  style={{ color: 'var(--status-danger)', alignSelf: 'center' }}
                >
                  {(create.error as Error).message}
                </span>
              ) : null}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
